/* LHC Buchung — Custom Booking-UI auf Basis der Sesami-Storefront-API.
   <lhc-booking-filter>  Kategorie-Filter + URL-Vorauswahl der Übersichtsseite (reine Anzeige-Logik).
   <lhc-booking-termin>  Wizard: Termin → (Kontaktdaten → Bestätigen → Fertig) bzw. → Checkout.

   Zwei Wege, die Weiche stellt config.instantBooking.enabled (pro Service im Sesami-Admin):
   A) Sofortbuchung AUS → Reservierung → /cart/add.js → Shopify-Checkout (Zahlung online)
   B) Sofortbuchung AN  → Kontaktdaten → POST /api/v1/book → eigene Erfolgsseite (Zahlung vor Ort)

   Endpoints (am Live-Shop verifiziert):
   1. GET  storefront.sesami.co/storefront?url=<b64(pageUrl)>&shopId=…&target[0][productId]=…  → { accessToken }
   2. GET  storefront.sesami.co/api/v1/config/<productId>?&token=<jwt>                          → { locations, translations, instantBooking, … }
   3. GET  storefront.sesami.co/availabilities?length=7&start=YYYY-MM-DD&accesstoken=…          → [{ date, slots: [{ startTime, status, … }] }]
   4a POST app.sesami.co/api/v1/reservation?accesstoken=…                                       → { token }   (Weg A)
   4b POST app.sesami.co/api/v1/book  (Bearer)                                                  → { _id, customerManagementLink, orderName, … }  (Weg B)

   Hinweis: /api/v1/book liegt NUR auf app.sesami.co — storefront.sesami.co antwortet dort 404. */

const SESAMI_STOREFRONT = 'https://storefront.sesami.co';
const SESAMI_APP = 'https://app.sesami.co';
const TOKEN_CACHE_KEY = 'lhc:sesami-token';
const TOKEN_CACHE_TTL = 60 * 60 * 1000; // 1h — der JWT selbst lebt länger
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
// /availabilities antwortet ab length=36 mit HTTP 500 — verifiziert.
const CHUNK_DAYS = 35;
// Wie weit maximal nach einem freien Tag gesucht wird (3 Chunks ≈ 15 Wochen).
const MAX_LOOKAHEAD_CHUNKS = 3;

class LhcBookingFilter extends HTMLElement {
  connectedCallback() {
    this.chips = Array.from(this.querySelectorAll('.lhc-bk-chip'));
    this.cards = Array.from(this.querySelectorAll('.lhc-bk-card'));
    this.empty = this.querySelector('.lhc-bk-grid__empty');
    this.chips.forEach((chip) =>
      chip.addEventListener('click', () => {
        this.apply(chip);
        this.syncUrl(chip);
      })
    );
    this.applyUrlPreselect();
  }

  apply(activeChip) {
    const filter = activeChip.dataset.filter || '';
    this.chips.forEach((chip) => {
      const active = chip === activeChip;
      chip.classList.toggle('is-active', active);
      chip.setAttribute('aria-pressed', String(active));
    });
    let visible = 0;
    this.cards.forEach((card) => {
      const show = !filter || card.dataset.category === filter;
      card.hidden = !show;
      if (show) visible += 1;
    });
    if (this.empty) this.empty.hidden = visible > 0;
  }

  /**
   * Deeplinks auf die Übersicht: ?select=<produkt-handle> filtert auf die Kategorie
   * der Anwendung und hebt deren Karte hervor, ?kategorie=<name> setzt nur den
   * Filter-Chip. Die Kategorie zu ?select kommt aus der Karte (data-category), nicht
   * aus der URL — so trifft der Link auch in übersetzten Sprachversionen den richtigen
   * Chip. Unbekannte Werte werden ignoriert: die Seite bleibt die normale Übersicht
   * (Query-Parameter erzeugen kein 404).
   */
  applyUrlPreselect() {
    const params = new URLSearchParams(window.location.search);
    const handle = (params.get('select') || '').trim().toLowerCase();
    const card = handle ? this.cards.find((c) => (c.dataset.handle || '') === handle) : null;
    const category = card
      ? card.dataset.category || ''
      : (params.get('kategorie') || '').trim().toLowerCase().replace(/\s+/g, '-');
    if (category) {
      const chip = this.chips.find((c) => (c.dataset.filter || '') === category);
      if (chip) this.apply(chip);
    }
    if (!card) return;
    card.classList.add('is-selected');
    card.setAttribute('aria-current', 'true');
    const box = card.getBoundingClientRect();
    const topOutOfView = box.top < 0 || box.top > window.innerHeight * 0.75;
    if (topOutOfView) card.scrollIntoView({ block: 'center' });
  }

  /**
   * Filtert der Nutzer selbst, trägt die URL den sichtbaren Zustand: die Kategorie
   * ersetzt die Vorauswahl aus dem Link, "Alle" räumt beide Parameter ab. Die Seite
   * bleibt damit jederzeit teilbar. Bewusst replaceState statt pushState — der Filter
   * ist keine Station der Buchungsstrecke, "Zurück" soll die Seite verlassen.
   */
  syncUrl(activeChip) {
    const selected = this.querySelector('.lhc-bk-card.is-selected');
    if (selected) {
      selected.classList.remove('is-selected');
      selected.removeAttribute('aria-current');
    }
    const filter = activeChip.dataset.filter || '';
    const url = new URL(window.location.href);
    url.searchParams.delete('select');
    if (filter) url.searchParams.set('kategorie', filter);
    else url.searchParams.delete('kategorie');
    history.replaceState(history.state, '', url);
  }
}

if (!customElements.get('lhc-booking-filter')) {
  customElements.define('lhc-booking-filter', LhcBookingFilter);
}

class LhcBookingTermin extends HTMLElement {
  connectedCallback() {
    this.shopId = this.dataset.shopId;
    this.productId = this.dataset.productId;
    this.variantId = this.dataset.variantId;
    this.locale = this.dataset.locale || 'de';
    this.checkoutUrl = this.dataset.checkoutUrl || '/checkout';
    this.cartAddUrl = this.dataset.cartAddUrl || '/cart/add.js';
    this.cartUrl = this.dataset.cartUrl || '/cart.js';
    this.cartChangeUrl = this.dataset.cartChangeUrl || '/cart/change.js';
    this.backUrl = this.dataset.backUrl || '/pages/buchen';
    this.productTitle = this.dataset.productTitle || '';
    this.priceCents = Number(this.dataset.priceCents) || 0;
    // Varianten-Metafeld custom.buchbar_nur_am: nur diese Tage sind buchbar,
    // alle anderen verhalten sich wie Tage ohne freie Slots. Leer = keine Sperre.
    this.onlyDates = (this.dataset.onlyDates || '')
      .split(',')
      .map((d) => d.trim())
      .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d));
    // Mit regulärer Variante daneben sperrt buchbar_nur_am nichts, sondern die
    // Terminwahl nimmt an diesen Tagen die Tagesvariante, sonst die reguläre.
    this.dayVariant = null;
    this.regularVariant = null;
    if (this.onlyDates.length && this.dataset.regularVariantId && this.dataset.dayVariantId) {
      this.dayVariant = {
        id: String(this.dataset.dayVariantId),
        priceCents: Number(this.dataset.dayPriceCents) || 0,
        duration: this.dataset.dayDuration || '',
      };
      this.regularVariant = {
        id: String(this.dataset.regularVariantId),
        priceCents: Number(this.dataset.regularPriceCents) || 0,
        duration: this.dataset.regularDuration || '',
      };
    }
    this.currency = this.dataset.currency || 'EUR';
    this.quantity = 1;
    this.groupMax = 1;
    this.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    this.sessionId = Math.random().toString(36).slice(2, 10);

    /** @type {Map<string, Array<{startTime: string, status: string}>>} */
    this.days = new Map();
    /** @type {Map<string, Promise<void>>} */
    this.inflight = new Map();
    /** Im laufenden Besuch angelegte Reservierungen — beim Retry wiederverwenden. */
    this.reservations = new Map();
    this.selectedDate = this.todayStr();
    this.selectedTime = null;
    this.booking = false;
    this.ready = false;
    this.instant = false;
    this.step = 'termin';
    this.calendarOpen = false;
    this.calendarMonth = null; // erster Tag des angezeigten Monats
    this.focusedDate = null; // Roving-Tabindex im Kalenderraster

    this.refs = {};
    this.querySelectorAll('[data-ref]').forEach((el) => {
      this.refs[el.dataset.ref] = el;
    });

    // Zubuchbare Anwendungen (Session-Upsell) — nur vorhanden, wenn das Produkt
    // einen "upsell:<collection>"-Tag trägt und die Section die Liste rendert.
    this.upsell = Array.from(this.querySelectorAll('[data-upsell]')).map((el) => ({
      el,
      productId: el.dataset.productId,
      variantId: el.dataset.variantId,
      priceCents: Number(el.dataset.priceCents) || 0,
      title: el.dataset.title || '',
      qty: 0,
      max: 0,
      days: new Map(),
      inflight: new Map(),
      times: [], // je Einheit (= eine Person) eine eigene Uhrzeit im Session-Fenster
      duration: 0,
      availEl: el.querySelector('[data-avail]'),
      countEl: el.querySelector('[data-count]'),
      plusEl: el.querySelector('[data-plus]'),
      minusEl: el.querySelector('[data-minus]'),
      timesEl: el.querySelector('[data-times]'),
      timeGridEl: el.querySelector('[data-time-grid]'),
      timesLabelEl: el.querySelector('[data-times-label]'),
      priceEl: el.querySelector('[data-upsell-price]'),
    }));
    this.panels = {};
    this.querySelectorAll('[data-panel]').forEach((el) => {
      this.panels[el.dataset.panel] = el;
    });
    try {
      this.copy = JSON.parse(this.refs.copy?.textContent || '{}');
    } catch (error) {
      this.copy = {};
    }
    // Aktionsrabatte nach Termindatum (Shop-Metafeld, siehe Section). Nur für die
    // Anzeige — abgezogen wird der Rabatt erst im Warenkorb durch die Rabatt-App.
    try {
      const raw = JSON.parse(this.refs.promos?.textContent || '[]');
      this.promos = (Array.isArray(raw) ? raw : [])
        .filter((p) => p && p.von && p.bis && (Number(p.prozent) > 0 || p.nurSession === true))
        .map((p) => ({
          von: String(p.von),
          bis: String(p.bis),
          prozent: Number(p.prozent) || 0,
          label: p.label || '',
          nurSession: p.nurSession === true,
          sessionUrl: typeof p.sessionUrl === 'string' ? p.sessionUrl : '',
          // Optional: exakte Produkt-IDs statt "alle aus der Übersicht".
          // Zahlen wie Strings akzeptieren — verglichen wird als String.
          produkte: Array.isArray(p.produkte) ? p.produkte.map(String) : [],
          // Optional: Aktion nur für diese Varianten-IDs (schlägt "produkte").
          varianten: Array.isArray(p.varianten) ? p.varianten.map(String) : [],
          // Optional: Zeitfenster, in dem die Aktion gebucht werden kann (ISO mit Offset).
          aktivAb: p.aktivAb ? Date.parse(p.aktivAb) : NaN,
          aktivBis: p.aktivBis ? Date.parse(p.aktivBis) : NaN,
        }));
    } catch (error) {
      this.promos = [];
    }

    this.bindEvents();

    this.init();
  }

  bindEvents() {
    const r = this.refs;
    r.prevDay?.addEventListener('click', () => this.shiftDay(-1));
    r.nextDay?.addEventListener('click', () => this.shiftDay(1));
    r.dateField?.addEventListener('click', () => this.toggleCalendar());
    r.calPrev?.addEventListener('click', () => this.shiftMonth(-1));
    r.calNext?.addEventListener('click', () => this.shiftMonth(1));
    r.calGrid?.addEventListener('click', (event) => {
      const cell = event.target.closest('[data-date]');
      if (!cell || cell.getAttribute('aria-disabled') === 'true') return;
      this.closeCalendar(true);
      this.goToDate(cell.dataset.date);
    });
    r.calGrid?.addEventListener('keydown', (event) => this.onCalendarKey(event));
    r.calendar?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.closeCalendar(true);
      }
    });
    this._onDocClick = (event) => {
      if (this.calendarOpen && !this.querySelector('.lhc-bk-datepicker')?.contains(event.target)) {
        this.closeCalendar(false);
      }
    };
    document.addEventListener('click', this._onDocClick);
    // Heraustabben schließt den Kalender, sonst verdeckt er den nächsten Fokus.
    this.querySelector('.lhc-bk-datepicker')?.addEventListener('focusout', (event) => {
      if (!this.calendarOpen) return;
      const next = event.relatedTarget;
      if (next && this.querySelector('.lhc-bk-datepicker')?.contains(next)) return;
      if (!next) return; // Fokusverlust ohne Ziel (z. B. Klick ins Nichts) ignorieren
      this.closeCalendar(false);
    });
    r.submit?.addEventListener('click', () => this.onSubmit());
    r.qty?.addEventListener('lhc-dropdown:change', (e) => this.setQuantity(e.detail.value));
    this.upsell.forEach((item) => {
      item.plusEl?.addEventListener('click', () => this.changeUpsellQty(item, 1));
      item.minusEl?.addEventListener('click', () => this.changeUpsellQty(item, -1));
    });
    r.back?.addEventListener('click', (event) => this.onBack(event));
    r.changeDate?.addEventListener('click', () => this.setStep('termin'));
    r.editData?.addEventListener('click', () => this.setStep('daten'));
    r.openPolicy?.addEventListener('click', () => this.refs.policy?.showModal());
    r.closePolicy?.addEventListener('click', () => this.refs.policy?.close());

    // Fehler am Feld verschwinden, sobald korrigiert wird
    ['firstName', 'lastName', 'email', 'phoneNumber'].forEach((key) => {
      r[key]?.addEventListener('input', () => {
        const field = r[key].dataset.field;
        this.clearFieldError(field);
      });
    });
    r.agb?.addEventListener('change', () => this.clearFieldError('agb'));

    // Nach Browser-Zurück aus dem bfcache hängt sonst "Wird gebucht …" fest.
    this._onPageShow = (event) => {
      if (!event.persisted) return;
      this.booking = false;
      // Nach Browser-Zurück können die Tokens bereits in einer Bestellung
      // verbraucht sein — nie wiederverwenden.
      this.reservations.clear();
      this.refs.submit?.removeAttribute('aria-busy');
      this.renderCta();
    };
    window.addEventListener('pageshow', this._onPageShow);

    this._onPopState = (event) => {
      const step = event.state?.lhcStep;
      if (!step || step === 'fertig') return;
      // Nach der Buchung bleibt die Erfolgsseite stehen — Browser-Zurück soll kein
      // zweites Formular für denselben Termin anbieten.
      if (this.step === 'fertig') return;
      // Der Eintrag kann älter sein als die aktuelle Wahl (z. B. inzwischen anderer
      // Tag gewählt → Uhrzeit verworfen). Dann nicht blind in den Schritt springen.
      let target = step;
      if (target !== 'termin' && !this.selectedTime) target = 'termin';
      else if (target === 'bestaetigen' && !this.validateForm()) target = 'daten';
      if (target !== step) {
        const url = new URL(window.location.href);
        if (target === 'termin') url.searchParams.delete('step');
        else url.searchParams.set('step', target);
        history.replaceState({ ...history.state, lhcStep: target }, '', url);
      }
      if (target === 'daten') this.fillSummaryForData();
      if (target === 'bestaetigen') this.fillConfirm();
      this.setStep(target, false);
    };
    window.addEventListener('popstate', this._onPopState);
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this._onPageShow);
    window.removeEventListener('popstate', this._onPopState);
    document.removeEventListener('click', this._onDocClick);
  }

  async init() {
    // Kein renderDateLabel() vorab — sonst steht „heute" da, als wäre es gewählt.
    // Das Feld zeigt bis zum ersten goToDate() das „Laden …" aus dem Markup.
    // Die Seite startet immer auf der Terminwahl — den Eintrag als solchen
    // markieren, sonst findet Browser-Zurück aus „daten" hierher kein Panel.
    history.replaceState({ ...history.state, lhcStep: 'termin' }, '', window.location.href);
    try {
      this.token = await this.fetchToken();
      this.config = await this.withAuthRetry(() => this.fetchConfig());
      this.location = this.config?.locations?.[0] || null;
      this.instant = !!this.config?.instantBooking?.enabled;
      // Telefon ist bei Sesami bewusst KEIN Pflichtfeld mehr: Sesami ordnet den
      // Shopify-Kunden sonst per Telefonnummer zu (vor der E-Mail) — gleiche Nummer
      // bei zwei Kunden = Bestellung und Bestätigung beim falschen Kunden (#LHS1566).
      // Ohne Pflicht-Telefon matcht Sesami per E-Mail. Wir wollen die Nummer trotzdem
      // immer haben, deshalb ist sie hier im Theme fest Pflicht.
      const fields = this.config?.instantBooking?.requiredFields || ['firstName', 'lastName', 'email'];
      this.requiredFields = fields.includes('phone') ? fields : [...fields, 'phone'];
      if (this.location?.name) {
        if (this.refs.locationLabel) this.refs.locationLabel.textContent = this.location.name;
        if (this.refs.confirmLocation) this.refs.confirmLocation.textContent = this.location.name;
      }
      if (!this.timezone) this.timezone = this.location?.timezone || 'Europe/Berlin';
      // Gruppenbuchung: Sesami meldet die Kapazität pro Slot als groupAppointment.
      this.groupMax = Math.max(1, Number(this.config?.product?.groupAppointment) || 1);
      this.renderQuantity();
      // Bedienelemente erst freigeben, wenn Token + Config da sind.
      this.ready = true;
      if (this.refs.dateField) this.refs.dateField.disabled = false;
      this.renderCta();

      // Nicht stumpf „heute" vorauswählen: viele Anwendungen sind erst in einigen
      // Tagen wieder frei. Die Vorlaufzeit filtert Sesami bereits serverseitig weg.
      this.renderSlots(undefined);
      // Das Datumsfeld ist ab jetzt bedienbar. Wählt der Nutzer schneller selbst
      // einen Tag, als die Startsuche fertig wird, gilt seine Wahl.
      const initial = this.selectedDate;
      // Personenzahl VOR der ersten Suche setzen — sie filtert die Slots serverseitig.
      const wish = this.readUrlWish();
      const qtyProblem = this.applyUrlQuantity(wish);
      const first = await this.findFirstAvailable(this.todayStr());
      if (this.selectedDate !== initial) return;
      if (!first) {
        this.noAvailability = true;
        this.renderDateLabel();
        this.renderSlots([]);
        this.renderCta();
        this.showMessage(
          [
            qtyProblem,
            this.quantity > 1
              ? `Für ${this.quantity} Personen ist aktuell kein Termin frei. Bitte wähle eine kleinere Anzahl.`
              : this.groupMax > 1
                ? 'Für diese Veranstaltung sind aktuell alle Plätze vergeben. Bitte kontaktiere uns direkt.'
                : 'Für diese Anwendung sind derzeit keine Termine buchbar. Bitte kontaktiere uns direkt.',
          ]
            .filter(Boolean)
            .join(' ')
        );
        return;
      }
      await this.applyUrlWish(wish, first, qtyProblem);
    } catch (error) {
      console.error('[lhc-booking]', error);
      if (error && error.notAService) {
        this.showMessage('Diese Anwendung ist aktuell nicht online buchbar. Bitte kontaktiere uns direkt.');
      } else {
        this.showMessage('Die Verfügbarkeiten konnten nicht geladen werden. Bitte lade die Seite neu.');
      }
      this.renderSlots(null);
    } finally {
      // `step` nur in der URL lassen, wenn der Sprung auch stattfand — auf jedem
      // anderen Weg (kein freier Termin, Nutzer war schneller, Ladefehler) lügt sie sonst.
      if (this.step !== 'daten') this.dropUrlStep();
    }
  }

  /* ---------- Sesami API ---------- */

  tokenCacheKey() {
    return `${TOKEN_CACHE_KEY}:${this.shopId}:${this.productId}`;
  }

  invalidateToken() {
    try {
      sessionStorage.removeItem(this.tokenCacheKey());
    } catch (error) {
      /* Storage blockiert → egal */
    }
    this.token = null;
  }

  async fetchToken() {
    const cacheKey = this.tokenCacheKey();
    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
      if (
        cached &&
        cached.shopId === this.shopId &&
        cached.productId === this.productId &&
        cached.exp > Date.now()
      ) {
        return cached.token;
      }
    } catch (error) {
      /* Cache defekt → neu holen */
    }
    const pageUrl = btoa(unescape(encodeURIComponent(window.location.href)));
    const url = `${SESAMI_STOREFRONT}/storefront?url=${encodeURIComponent(pageUrl)}&shopId=${this.shopId}&target[0][productId]=${this.productId}`;
    const data = await this.getJson(url);
    if (!data?.accessToken) {
      // { status: false } = dieses Produkt ist kein Sesami-Service
      const error = new Error('Sesami: kein accessToken erhalten');
      error.notAService = data?.status === false;
      throw error;
    }
    try {
      sessionStorage.setItem(
        cacheKey,
        JSON.stringify({
          token: data.accessToken,
          shopId: this.shopId,
          productId: this.productId,
          exp: Date.now() + TOKEN_CACHE_TTL,
        })
      );
    } catch (error) {
      /* Storage voll/blockiert → ohne Cache weiter */
    }
    return data.accessToken;
  }

  // Bei 401/403 (Token abgelaufen/zurückgezogen) Cache verwerfen und genau einmal
  // mit frischem Token wiederholen — sonst hängt der Tab bis zu 1h in der Sackgasse.
  async withAuthRetry(run) {
    try {
      return await run();
    } catch (error) {
      if (error && (error.status === 401 || error.status === 403)) {
        this.invalidateToken();
        this.token = await this.fetchToken();
        return run();
      }
      throw error;
    }
  }

  fetchConfig() {
    return this.getJson(`${SESAMI_STOREFRONT}/api/v1/config/${this.productId}?&token=${encodeURIComponent(this.token)}`);
  }

  // Pro Startdatum läuft höchstens ein Fetch; parallele goToDate()-Aufrufe
  // warten auf dieselbe Promise (sonst überschreibt ein verspäteter Fehler gute Daten).
  loadDays(startDate) {
    let pending = this.inflight.get(startDate);
    if (!pending) {
      pending = this.withAuthRetry(() => this.fetchDays(startDate)).finally(() => this.inflight.delete(startDate));
      this.inflight.set(startDate, pending);
    }
    return pending;
  }

  async fetchAvailability(startDate, length, variantId) {
    const params = new URLSearchParams({
      length: String(length),
      start: startDate,
      accesstoken: this.token,
      shopId: this.shopId,
      productId: this.productId,
      variantId,
      quantity: String(this.quantity),
      timezone: this.timezone,
    });
    if (this.location?.id) params.set('location', this.location.id);
    const data = await this.getJson(`${SESAMI_STOREFRONT}/availabilities?${params}`);
    if (!Array.isArray(data)) throw new Error('Sesami Availabilities: unerwartetes Antwortformat');
    return data;
  }

  async fetchDays(startDate, length = CHUNK_DAYS) {
    if (this.dayVariant) {
      const [regular, day] = await Promise.all([
        this.fetchAvailability(startDate, length, this.regularVariant.id),
        this.fetchAvailability(startDate, length, this.dayVariant.id),
      ]);
      for (let i = 0; i < length; i += 1) {
        this.days.set(this.addDays(startDate, i), []);
      }
      regular.forEach((d) => {
        if (d?.date && !this.onlyDates.includes(d.date)) this.days.set(d.date, d.slots || []);
      });
      day.forEach((d) => {
        if (d?.date && this.onlyDates.includes(d.date)) this.days.set(d.date, d.slots || []);
      });
      return;
    }
    const data = await this.fetchAvailability(startDate, length, this.variantId);
    // Alle angefragten Tage zurücksetzen — Tage ohne Slots fehlen in der Antwort.
    // Bewusst ohne has()-Guard: sonst behielte ein inzwischen ausgebuchter Tag
    // beim erneuten Abruf seine veralteten Zeiten.
    for (let i = 0; i < length; i += 1) {
      this.days.set(this.addDays(startDate, i), []);
    }
    data.forEach((day) => {
      if (!day?.date) return;
      if (this.onlyDates.length && !this.onlyDates.includes(day.date)) return;
      this.days.set(day.date, day.slots || []);
    });
  }

  async getJson(url) {
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) {
      const error = new Error(`Sesami ${response.status} für ${url.split('?')[0]}`);
      error.status = response.status;
      throw error;
    }
    return response.json();
  }

  /* ---------- Datum ---------- */

  todayStr() {
    return new Date().toLocaleDateString('en-CA');
  }

  addDays(dateStr, n) {
    const date = new Date(`${dateStr}T12:00:00`);
    date.setDate(date.getDate() + n);
    return date.toLocaleDateString('en-CA');
  }

  formatDate(dateStr) {
    const date = new Date(`${dateStr}T12:00:00`);
    const weekday = date.toLocaleDateString(this.locale, { weekday: 'long' });
    const formatted = date.toLocaleDateString(this.locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
    return `${weekday}, ${formatted}`;
  }

  async goToDate(dateStr) {
    if (!this.ready) return;
    if (dateStr < this.todayStr()) dateStr = this.todayStr();
    this.selectedDate = dateStr;
    this.selectedTime = null;
    this.applyVariantForDate(dateStr);
    this.exhaustedAhead = false;
    this.renderDateLabel();
    // Aktionspreise hängen am Termindatum, nicht an der Uhrzeit — sofort umstellen,
    // damit der Kunde den Aktionspreis schon beim Blättern durch die Tage sieht.
    this.renderPromoPrices();
    // Session-exklusive Tage: statt der Slots ein Hinweis mit Link zur Session.
    // Kein Verfügbarkeits-Request — buchbar ist hier ohnehin nichts.
    const sessionOnly = this.sessionOnlyInfo(dateStr);
    this.sessionOnlyDay = Boolean(sessionOnly);
    if (sessionOnly) {
      this.clearMessage();
      this.renderSessionOnly(sessionOnly);
      this.renderCta();
      return;
    }
    this.renderCta();
    if (!this.days.has(dateStr)) {
      this.renderSlots(undefined); // Ladezustand
      try {
        await this.loadDays(dateStr);
      } catch (error) {
        console.error('[lhc-booking]', error);
        // Nur melden, wenn der Tag noch angezeigt wird UND nicht inzwischen
        // von einem parallelen Request erfolgreich geladen wurde.
        if (dateStr === this.selectedDate && !this.days.has(dateStr)) {
          this.showMessage('Die Verfügbarkeiten konnten nicht geladen werden. Bitte versuche es erneut.');
          this.renderSlots(null);
        }
        return;
      }
    }
    if (dateStr === this.selectedDate) {
      this.clearMessage();
      this.renderSlots(this.days.get(dateStr));
      this.refreshUpsell(); // selectedTime ist zurückgesetzt → Stepper sperren
    }
  }

  /** Hat der Tag mindestens einen buchbaren Slot? Nur sinnvoll, wenn er geladen ist. */
  hasSlots(dateStr) {
    const slots = this.days.get(dateStr);
    if (!slots) return false;
    return slots.some((s) => s.status === 'available' && s.remainingSlots !== 0);
  }

  /**
   * Sucht ab `from` vorwärts den ersten Tag mit freien Slots und lädt dabei
   * chunkweise nach. Gibt null zurück, wenn im Suchfenster nichts frei ist.
   * Wirft, wenn das Nachladen fehlschlägt — null heißt ausschließlich
   * „nichts frei", nie „Ladefehler".
   */
  async findFirstAvailable(from) {
    let cursor = from;
    for (let chunk = 0; chunk < MAX_LOOKAHEAD_CHUNKS; chunk += 1) {
      await this.loadDays(cursor);
      for (let i = 0; i < CHUNK_DAYS; i += 1) {
        const date = this.addDays(cursor, i);
        if (this.bookableDay(date)) return date;
      }
      cursor = this.addDays(cursor, CHUNK_DAYS);
    }
    return null;
  }

  /** Nächster/vorheriger Tag mit freien Slots innerhalb des geladenen Bereichs. */
  nextAvailableDay(from, direction) {
    const today = this.todayStr();
    let date = from;
    for (let i = 0; i < CHUNK_DAYS * MAX_LOOKAHEAD_CHUNKS; i += 1) {
      date = this.addDays(date, direction);
      if (direction < 0 && date < today) return null;
      if (!this.days.has(date)) return direction > 0 ? date : null; // ungeladen → vorwärts nachladen
      if (this.bookableDay(date)) return date;
    }
    return null;
  }

  async shiftDay(direction) {
    if (!this.ready) return;
    const target = this.nextAvailableDay(this.selectedDate, direction);
    if (!target) {
      if (direction > 0) {
        this.exhaustedAhead = true;
        this.renderDateLabel();
      }
      return;
    }
    if (direction > 0 && !this.hasSlots(target)) {
      // Grenze des geladenen Bereichs erreicht → weitersuchen
      let found;
      try {
        found = await this.findFirstAvailable(target);
      } catch (error) {
        console.error('[lhc-booking]', error);
        this.showMessage('Die Verfügbarkeiten konnten nicht geladen werden. Bitte versuche es erneut.');
        return;
      }
      if (!found) {
        this.exhaustedAhead = true;
        this.renderDateLabel();
        this.showMessage('Weiter vorn sind keine Termine mehr buchbar.');
        return;
      }
      await this.goToDate(found);
      return;
    }
    await this.goToDate(target);
  }

  renderDateLabel() {
    const { dateLabel, prevDay, nextDay } = this.refs;
    if (dateLabel) dateLabel.textContent = this.formatDate(this.selectedDate);
    const blocked = !this.ready || this.noAvailability;
    if (prevDay) prevDay.disabled = blocked || !this.nextAvailableDay(this.selectedDate, -1);
    if (nextDay) nextDay.disabled = blocked || this.exhaustedAhead === true;
  }

  /* ---------- Gruppenbuchung / Menge ---------- */

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currency }).format(cents / 100);
    } catch (error) {
      return `${(cents / 100).toFixed(2)} ${this.currency}`;
    }
  }

  /** Kostenlose Termine als solche benennen statt „0,00 €" anzuzeigen. */
  priceLabel(cents) {
    return cents > 0 ? this.formatMoney(cents) : 'Kostenlos';
  }

  renderQuantity() {
    const { qtyBox, qty, qtyHint } = this.refs;
    if (!qtyBox) return;
    if (this.groupMax <= 1) {
      qtyBox.hidden = true;
      this.updatePriceDisplay();
      return;
    }
    qtyBox.hidden = false;
    const panel = qty?.querySelector('[data-dd-panel]');
    if (panel && !panel.children.length) {
      for (let i = 1; i <= this.groupMax; i += 1) {
        const option = document.createElement('li');
        option.className = 'lhc-dd__option';
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');
        option.setAttribute('tabindex', '-1');
        option.dataset.ddOption = '';
        option.dataset.value = String(i);
        option.textContent = i === 1 ? '1 Person' : `${i} Personen`;
        panel.appendChild(option);
      }
      qty.selectValue?.(this.quantity);
    }
    if (qtyHint) qtyHint.textContent = `Bis zu ${this.groupMax} Personen pro Termin buchbar.`;
    this.updatePriceDisplay();
  }

  updatePriceDisplay() {
    const total = this.priceCents * this.quantity;
    const upsellTotal = this.upsellCents();
    const { summaryPrice, confirmTotal, successTotal, confirmQty, confirmQtyRow, successQty, successQtyRow } = this.refs;
    if (summaryPrice) {
      if (upsellTotal > 0) {
        const voll = total + this.upsellCentsFull();
        const jetzt = total + upsellTotal;
        summaryPrice.textContent = '';
        if (jetzt < voll) {
          const was = document.createElement('s');
          was.className = 'lhc-bk-price__was';
          was.textContent = this.formatMoney(voll);
          const now = document.createElement('span');
          now.className = 'lhc-bk-price__now';
          now.textContent = `${this.formatMoney(jetzt)} gesamt`;
          summaryPrice.append(was, now);
        } else {
          summaryPrice.textContent = `${this.formatMoney(jetzt)} gesamt`;
        }
      } else {
        // Aktionspreis der Anwendung selbst. Nicht bei Sofortbuchung: dort läuft
        // keine Rabatt-App, der Kunde zahlt vor Ort den vollen Preis.
        const percent = this.instant || this.priceCents <= 0 ? 0 : this.promoPercent();
        const unit = this.discountedCents(this.priceCents, percent);
        const text =
          this.quantity > 1 && unit > 0
            ? `${this.formatMoney(unit * this.quantity)} (${this.quantity} × ${this.formatMoney(unit)})`
            : this.priceLabel(unit * this.quantity);
        summaryPrice.textContent = '';
        if (percent) {
          const was = document.createElement('s');
          was.className = 'lhc-bk-price__was';
          was.textContent = this.formatMoney(total);
          const sr = document.createElement('span');
          sr.className = 'visually-hidden';
          sr.textContent = `, reduziert um ${percent} Prozent auf`;
          const now = document.createElement('span');
          now.className = 'lhc-bk-price__now';
          now.textContent = text;
          summaryPrice.append(was, sr, now);
          summaryPrice.setAttribute('data-promo', String(percent));
        } else {
          summaryPrice.textContent = text;
          summaryPrice.removeAttribute('data-promo');
        }
        this.renderPromoNote(percent);
      }
    }
    if (confirmTotal) confirmTotal.textContent = this.priceLabel(total);
    if (successTotal) successTotal.textContent = this.priceLabel(total);
    const label = this.quantity === 1 ? '1 Person' : `${this.quantity} Personen`;
    if (confirmQty) confirmQty.textContent = label;
    if (successQty) successQty.textContent = label;
    // Bei Gruppenterminen immer zeigen, auch „1 Person" — sonst bleibt unsichtbar, dass gewählt werden konnte.
    if (confirmQtyRow) confirmQtyRow.hidden = this.groupMax <= 1;
    if (successQtyRow) successQtyRow.hidden = this.groupMax <= 1;
  }

  /** Menge ändern: Sesami filtert die Slots serverseitig, daher komplett neu laden. */
  async setQuantity(next) {
    const value = Math.min(Math.max(1, Number(next) || 1), this.groupMax);
    if (value === this.quantity) return;
    this.quantity = value;
    this.days.clear();
    this.inflight.clear();
    this.exhaustedAhead = false;
    this.noAvailability = false;
    this.selectedTime = null;
    this.refreshUpsell(); // Uhrzeit ist weg → Stepper sperren, Kontingent-Text zurücksetzen
    this.updatePriceDisplay();
    this.clearMessage();
    this.renderSlots(undefined);
    try {
      const keep = this.selectedDate < this.todayStr() ? this.todayStr() : this.selectedDate;
      await this.loadDays(keep);
      if (this.hasSlots(keep)) {
        await this.goToDate(keep);
        return;
      }
      const first = await this.findFirstAvailable(this.todayStr());
      if (!first) {
        this.noAvailability = true;
        this.renderDateLabel();
        this.renderSlots([]);
        this.renderCta();
        this.showMessage(`Für ${value} Personen ist aktuell kein Termin frei. Bitte wähle eine kleinere Anzahl.`);
        return;
      }
      await this.goToDate(first);
      this.showMessage(`Für ${value} Personen ist der nächste freie Termin am ${this.formatDate(first)}.`);
    } catch (error) {
      console.error('[lhc-booking]', error);
      this.showMessage('Die Verfügbarkeiten konnten nicht geladen werden. Bitte versuche es erneut.');
    }
  }

  /* ---------- Vorauswahl über URL-Parameter ---------- */

  readUrlWish() {
    const p = new URLSearchParams(window.location.search);
    const pick = (...keys) => {
      for (const k of keys) {
        const v = p.get(k);
        if (v) return v.trim();
      }
      return null;
    };
    return { date: pick('datum', 'date'), time: pick('uhrzeit', 'time'), qty: pick('personen', 'quantity'), step: pick('step') };
  }

  /** Merker aus bookInstant: wurde genau dieser Termin in diesem Tab schon gebucht? */
  wasBookedHere(date, time) {
    try {
      return sessionStorage.getItem(`lhc:booked:${this.productId}`) === `${date}|${time}`;
    } catch (error) {
      return false;
    }
  }

  /** `step` aus der URL nehmen, wenn der Wunsch nicht erfüllt wurde — die URL soll nicht lügen. */
  dropUrlStep() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('step')) return;
    url.searchParams.delete('step');
    history.replaceState(history.state, '', url);
  }

  /** Personenzahl aus der URL übernehmen. Gibt eine Meldung zurück, wenn sie nicht passt. */
  applyUrlQuantity(wish) {
    if (!wish?.qty) return null;
    const n = Number(wish.qty);
    if (!Number.isInteger(n) || n < 1) return 'Die gewünschte Personenzahl aus dem Link ist ungültig.';
    if (n > this.groupMax) {
      return this.groupMax > 1
        ? `Für diesen Termin sind höchstens ${this.groupMax} Personen buchbar.`
        : 'Diese Anwendung kann nur für eine Person gebucht werden.';
    }
    if (n > 1) {
      this.quantity = n;
      this.refs.qty?.selectValue?.(n);
      this.updatePriceDisplay();
    }
    return null;
  }

  /**
   * Wendet Datum/Uhrzeit aus der URL an. Jeder ungültige Wunsch fällt auf die
   * normale Vorauswahl zurück und erklärt dem Nutzer, warum.
   */
  async applyUrlWish(wish, fallbackDate, qtyProblem) {
    const problems = qtyProblem ? [qtyProblem] : [];
    // Wählt der Nutzer während der Requests selbst einen Tag, gilt seine Wahl:
    // dann weder überschreiben noch nach „daten" springen.
    const initial = this.selectedDate;
    if (!wish || (!wish.date && !wish.time)) {
      await this.goToDate(fallbackDate);
      if (problems.length) this.showMessage(problems.join(' '));
      return;
    }

    let target = null;
    if (wish.date) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(wish.date) || Number.isNaN(Date.parse(`${wish.date}T12:00:00`))) {
        problems.push('Das Datum aus dem Link ist ungültig.');
      } else if (wish.date < this.todayStr()) {
        problems.push('Der Termin aus dem Link liegt in der Vergangenheit.');
      } else if (this.sessionOnlyInfo(wish.date)) {
        // Gewünschter Tag ist session-exklusiv: dort landen (das Panel erklärt
        // die Lage samt Link), aber keinen Zeitwunsch beantworten und keine
        // Verfügbarkeiten laden — einzeln buchbar ist an dem Tag nichts.
        target = wish.date;
        wish.time = null;
      } else {
        try {
          await this.loadDays(wish.date);
          if (this.hasSlots(wish.date)) target = wish.date;
          else problems.push(`Am ${this.formatDate(wish.date)} sind keine Termine mehr frei.`);
        } catch (error) {
          console.error('[lhc-booking]', error);
          problems.push('Die Verfügbarkeiten für das Datum aus dem Link konnten nicht geladen werden.');
        }
      }
    }

    if (this.selectedDate !== initial) return;
    // Bei geänderter Personenzahl kann der Fallback veraltet sein → neu suchen.
    let landing = target;
    if (!landing) {
      landing = this.hasSlots(fallbackDate) ? fallbackDate : await this.findFirstAvailable(this.todayStr());
      if (this.selectedDate !== initial) return;
    }
    if (!landing) {
      this.noAvailability = true;
      this.renderDateLabel();
      this.renderSlots([]);
      this.renderCta();
      this.showMessage('Für diese Anwendung sind derzeit keine Termine buchbar. Bitte kontaktiere uns direkt.');
      return;
    }
    await this.goToDate(landing);
    if (this.selectedDate !== landing) return;

    if (target && wish.time) {
      if (!/^\d{1,2}:\d{2}$/.test(wish.time)) {
        problems.push('Die Uhrzeit aus dem Link ist ungültig.');
      } else {
        const wanted = wish.time.padStart(5, '0');
        const chip = [...(this.refs.grid?.querySelectorAll('.lhc-bk-slot') || [])].find(
          (c) => c.textContent === wanted
        );
        if (chip) this.selectSlot(chip, wanted);
        else {
          problems.push(
            this.quantity > 1
              ? `Um ${wanted} Uhr sind nicht mehr genug Plätze für ${this.quantity} Personen frei.`
              : `Der Termin um ${wanted} Uhr ist nicht mehr verfügbar. Bitte wähle eine andere Uhrzeit.`
          );
        }
      }
    }

    // `step=daten`: Datum UND Uhrzeit kamen gültig aus dem Link → direkt zu den
    // Kontaktdaten, die Terminwahl bleibt per „Zurück" erreichbar. Nur bei
    // Sofortbuchung — im Checkout-Weg gibt es den Schritt nicht. Jeder Mangel am
    // Wunsch lässt den Nutzer auf der Terminwahl, mit der Erklärung von oben.
    // Produkte mit Behandlungsliste (Session-Upsell) springen nie — die Liste
    // steht nur auf der Terminwahl. Ein in diesem Tab schon gebuchter Termin
    // (Reload nach Browser-Zurück) bekommt das Formular nicht ein zweites Mal direkt.
    if (
      wish.step === 'daten' &&
      !problems.length &&
      target &&
      this.selectedTime &&
      this.instant &&
      this.upsell.length === 0 &&
      !this.wasBookedHere(target, this.selectedTime)
    ) {
      this.fillSummaryForData();
      this.setStep('daten', false);
      // replaceState statt pushState: der Link IST der Einstieg, „Zurück" im Browser
      // soll die Seite verlassen, nicht erst auf die Terminwahl springen.
      history.replaceState({ lhcStep: 'daten' }, '', window.location.href);
      return;
    }

    if (problems.length) this.showMessage(problems.join(' '));
  }

  /* ---------- Kalender ---------- */

  monthStart(dateStr) {
    return `${dateStr.slice(0, 7)}-01`;
  }

  toggleCalendar() {
    if (this.calendarOpen) this.closeCalendar(true);
    else this.openCalendar();
  }

  async openCalendar() {
    if (!this.ready) return;
    const { calendar, dateField } = this.refs;
    if (!calendar) return;
    this.calendarOpen = true;
    this.calendarMonth = this.monthStart(this.selectedDate);
    this.focusedDate = this.selectedDate;
    calendar.hidden = false;
    dateField?.setAttribute('aria-expanded', 'true');
    this.renderCalendar();
    // Fokus sofort setzen, nicht erst nach dem Netzwerk-Request.
    this.focusGrid();
    await this.ensureMonthLoaded();
    if (!this.calendarOpen) return;
    this.renderCalendar();
    this.focusGrid();
  }

  /** Fokussiert die aktive Rasterzelle — nur, wenn der Fokus ohnehin im Kalender liegt. */
  focusGrid(force = false) {
    const { calendar, calGrid } = this.refs;
    if (!calGrid || !this.calendarOpen) return;
    const inside = calendar?.contains(document.activeElement);
    const lost = !document.activeElement || document.activeElement === document.body;
    if (!force && !inside && !lost) return;
    calGrid.querySelector('[tabindex="0"]')?.focus();
  }

  closeCalendar(returnFocus) {
    const { calendar, dateField } = this.refs;
    if (!calendar || !this.calendarOpen) return;
    this.calendarOpen = false;
    calendar.hidden = true;
    dateField?.setAttribute('aria-expanded', 'false');
    if (returnFocus) dateField?.focus();
  }

  /** Lädt alle Tage des sichtbaren Monats (max. 31 → passt in einen Chunk). */
  async ensureMonthLoaded() {
    const first = this.calendarMonth;
    const last = this.addDays(this.monthStart(this.addDays(first, 32)), -1);
    let cursor = first < this.todayStr() ? this.todayStr() : first;
    const { calNote } = this.refs;
    // Blättert der Nutzer weiter, während noch geladen wird, darf der alte Lauf
    // weder die Notiz noch das Raster des neuen Monats anfassen.
    const run = (this._monthRun = (this._monthRun || 0) + 1);
    const mine = () => this._monthRun === run && this.calendarOpen && this.calendarMonth === first;
    if (calNote) {
      calNote.textContent = 'Verfügbarkeiten werden geladen …';
      calNote.hidden = false;
    }
    try {
      // loadDays markiert CHUNK_DAYS Tage ab cursor als bekannt — nach spätestens
      // einem Nachladen ist der Rest des Monats abgedeckt.
      while (cursor <= last) {
        if (!this.days.has(cursor)) await this.loadDays(cursor);
        if (!mine()) return;
        cursor = this.addDays(cursor, 1);
      }
      if (calNote && mine()) calNote.hidden = true;
    } catch (error) {
      console.error('[lhc-booking]', error);
      if (calNote && mine()) {
        calNote.textContent = 'Verfügbarkeiten konnten nicht geladen werden.';
        calNote.hidden = false;
      }
    }
  }

  async shiftMonth(direction) {
    const base = this.addDays(this.calendarMonth, direction > 0 ? 32 : -1);
    const target = this.monthStart(base);
    if (direction < 0 && target < this.monthStart(this.todayStr())) return;
    this.calendarMonth = target;
    this.focusedDate = null;
    this.renderCalendar();
    // Der geklickte Pfeil kann sich selbst deaktiviert haben — dann fiele der
    // Fokus auf <body>. Erst nach dem Rendern einsammeln.
    this.focusGrid();
    await this.ensureMonthLoaded();
    if (!this.calendarOpen || this.calendarMonth !== target) return;
    this.renderCalendar();
    this.focusGrid();
  }

  renderCalendar() {
    const { calGrid, calMonth, calPrev } = this.refs;
    if (!calGrid || !this.calendarMonth) return;
    const first = this.calendarMonth;
    const firstDate = new Date(`${first}T12:00:00`);
    if (calMonth) {
      calMonth.textContent = firstDate.toLocaleDateString(this.locale, { month: 'long', year: 'numeric' });
    }
    if (calPrev) calPrev.disabled = first <= this.monthStart(this.todayStr());

    // Montag als erster Wochentag
    const leading = (firstDate.getDay() + 6) % 7;
    const daysInMonth = new Date(firstDate.getFullYear(), firstDate.getMonth() + 1, 0).getDate();
    const today = this.todayStr();

    if (!this.focusedDate || this.monthStart(this.focusedDate) !== first) {
      this.focusedDate = this.monthStart(this.selectedDate) === first ? this.selectedDate : first;
    }

    calGrid.textContent = '';
    for (let i = 0; i < leading; i += 1) {
      const filler = document.createElement('span');
      filler.className = 'lhc-bk-cal__day lhc-bk-cal__day--empty';
      calGrid.appendChild(filler);
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = `${first.slice(0, 8)}${String(day).padStart(2, '0')}`;
      const cell = document.createElement('button');
      cell.type = 'button';
      cell.className = 'lhc-bk-cal__day';
      cell.dataset.date = date;
      cell.textContent = String(day);
      const selectable = date >= today && this.hasSlots(date);
      // Session-exklusive Tage bleiben wählbar — der Klick führt zum Hinweis-Panel
      // mit dem Link zur Session, statt den Tag kommentarlos zu sperren.
      const sessionOnly = selectable ? this.sessionOnlyInfo(date) : null;
      // aria-disabled statt disabled: Tastaturnutzer können den Monat abfahren.
      cell.setAttribute('aria-disabled', String(!selectable));
      // aria-current statt aria-selected: Letzteres wird auf role=button ignoriert.
      if (date === this.selectedDate) cell.setAttribute('aria-current', 'date');
      cell.setAttribute('aria-selected', String(date === this.selectedDate));
      cell.tabIndex = date === this.focusedDate ? 0 : -1;
      cell.setAttribute(
        'aria-label',
        `${this.formatDate(date)}${
          selectable ? (sessionOnly ? ' — nur als Teil einer Session buchbar' : '') : ' — keine Termine verfügbar'
        }${date === this.selectedDate ? ' — ausgewählt' : ''}`
      );
      calGrid.appendChild(cell);
    }
  }

  onCalendarKey(event) {
    const moves = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    let next = null;
    if (event.key in moves) next = this.addDays(this.focusedDate, moves[event.key]);
    else if (event.key === 'Home') next = this.calendarMonth;
    else if (event.key === 'End') next = this.addDays(this.monthStart(this.addDays(this.calendarMonth, 32)), -1);
    else if (event.key === 'PageUp' || event.key === 'PageDown') {
      event.preventDefault();
      // shiftMonth holt den Fokus am Ende selbst wieder ins Raster.
      this.shiftMonth(event.key === 'PageUp' ? -1 : 1);
      return;
    } else if (event.key === 'Enter' || event.key === ' ') {
      const cell = event.target.closest('[data-date]');
      if (cell && cell.getAttribute('aria-disabled') !== 'true') {
        event.preventDefault();
        this.closeCalendar(true);
        this.goToDate(cell.dataset.date);
      }
      return;
    } else {
      return;
    }
    event.preventDefault();
    const targetMonth = this.monthStart(next);
    // Nicht in vergangene Monate laufen — dieselbe Grenze wie bei den Pfeil-Buttons.
    if (targetMonth < this.monthStart(this.todayStr())) return;
    if (targetMonth !== this.calendarMonth) {
      this.calendarMonth = targetMonth;
      this.focusedDate = next;
      this.renderCalendar();
      this.focusGrid();
      this.ensureMonthLoaded().then(() => {
        if (!this.calendarOpen || this.calendarMonth !== targetMonth) return;
        this.renderCalendar();
        this.focusGrid();
      });
      return;
    }
    this.focusedDate = next;
    this.renderCalendar();
    this.focusGrid();
  }

  /* ---------- Slots ---------- */

  renderSlots(slots) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.textContent = '';
    if (slots === undefined) {
      grid.appendChild(this.note('Verfügbarkeiten werden geladen …'));
      return;
    }
    if (slots === null) {
      grid.appendChild(this.note('Keine Daten verfügbar.'));
      return;
    }
    const available = slots.filter((slot) => slot.status === 'available' && slot.remainingSlots !== 0);
    if (available.length === 0) {
      grid.appendChild(this.note('Keine verfügbaren Zeitfenster an diesem Tag.'));
      return;
    }
    available.forEach((slot) => {
      const time = (slot.startTime || '').split(' ')[1] || '';
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'lhc-bk-slot';
      chip.setAttribute('role', 'radio');
      chip.setAttribute('aria-checked', 'false');
      chip.textContent = time;
      chip.addEventListener('click', () => this.selectSlot(chip, time));
      grid.appendChild(chip);
    });
  }

  note(text) {
    const p = document.createElement('p');
    p.className = 'lhc-bk-slots__note';
    p.textContent = text;
    return p;
  }

  selectSlot(chip, time) {
    this.refs.grid?.querySelectorAll('.lhc-bk-slot').forEach((el) => el.setAttribute('aria-checked', 'false'));
    chip.setAttribute('aria-checked', 'true');
    this.selectedTime = time;
    this.clearMessage();
    this.refreshUpsell();
  }

  /* ---------- Zubuchbare Anwendungen (Session-Upsell) ---------- */

  upsellCount() {
    return this.upsell.reduce((sum, item) => sum + item.qty, 0);
  }

  upsellCents() {
    return this.upsell.reduce(
      (sum, item) =>
        sum +
        item.qty *
          this.discountedCents(item.priceCents, this.promoPercent(this.selectedDate, item.productId, item.variantId)),
      0
    );
  }

  /** Summe der zugebuchten Anwendungen ohne Aktionsrabatt. */
  upsellCentsFull() {
    return this.upsell.reduce((sum, item) => sum + item.qty * item.priceCents, 0);
  }

  /* --- Aktionsrabatte nach Termindatum --- */

  /**
   * Prozentsatz für das aktuell gewählte Termindatum, 0 wenn keine Aktion greift.
   * ISO-Daten (YYYY-MM-DD) sind lexikografisch sortiert, ein String-Vergleich
   * reicht also — genau wie in der Rabattregel der App.
   */
  promoPercent(date = this.selectedDate, productId = this.productId, variantId = this.variantId) {
    const hit = this.promoEntry(date, productId, variantId);
    return hit ? hit.prozent : 0;
  }

  /** Aktion gerade buchbar? Auf Staging/Dev (data-promo-preview) immer. */
  promoLive(p) {
    if (this.dataset.promoPreview === 'true') return true;
    const now = Date.now();
    if (!Number.isNaN(p.aktivAb) && now < p.aktivAb) return false;
    if (!Number.isNaN(p.aktivBis) && now > p.aktivBis) return false;
    return true;
  }

  /**
   * Passender Aktions-Eintrag. Einträge mit "varianten" oder "produkte" gelten nur
   * für genau diese IDs und haben Vorrang vor allgemeinen Einträgen (ohne Liste).
   * Bei nurSession-Einträgen ist "produkte" die Sperrliste, der Prozentsatz gilt
   * dort weiterhin für alle — wie bisher.
   */
  promoEntry(date = this.selectedDate, productId = this.productId, variantId = this.variantId) {
    if (!date) return null;
    const pid = String(productId || '');
    const vid = String(variantId || '');
    let best = null;
    let bestRank = -1;
    this.promos.forEach((p) => {
      if (!(p.prozent > 0 && date >= p.von && date <= p.bis) || !this.promoLive(p)) return;
      let rank = 0;
      if (p.varianten.length) {
        if (!p.varianten.includes(vid)) return;
        rank = 2;
      } else if (p.produkte.length && !p.nurSession) {
        if (!p.produkte.includes(pid)) return;
        rank = 1;
      }
      if (rank > bestRank) {
        best = p;
        bestRank = rank;
      }
    });
    return best;
  }

  /**
   * Session-exklusiver Zeitraum für dieses Datum — Anwendungen sind dann nur
   * als Zubuchung innerhalb einer Session buchbar, nicht einzeln. Gesperrt ist
   * genau, was im Metafeld-Eintrag unter "produkte" steht — sonst nichts.
   */
  sessionOnlyInfo(date = this.selectedDate) {
    if (!date) return null;
    return (
      this.promos.find(
        (p) => p.nurSession && date >= p.von && date <= p.bis && p.produkte.includes(String(this.productId))
      ) || null
    );
  }

  /** Tag hat Slots UND ist nicht session-exklusiv gesperrt. */
  bookableDay(date) {
    return this.hasSlots(date) && !this.sessionOnlyInfo(date);
  }

  /** Statt der Zeitslots: Erklärung plus Link zur Session-Buchung. */
  renderSessionOnly(info) {
    const grid = this.refs.grid;
    if (!grid) return;
    grid.textContent = '';
    const box = document.createElement('div');
    box.className = 'lhc-bk-sessiononly';
    const text = document.createElement('p');
    text.className = 'lhc-bk-sessiononly__text';
    text.textContent = `${info.label ? `${info.label}: ` : ''}An diesem Tag sind Anwendungen ausschließlich als Teil einer Session buchbar.`;
    box.appendChild(text);
    if (info.sessionUrl) {
      const link = document.createElement('a');
      link.className = 'lhc-bk-sessiononly__link';
      link.href = info.sessionUrl;
      link.textContent = 'Zur Session-Buchung';
      box.appendChild(link);
    }
    grid.appendChild(box);
  }

  /** Tagesvariante an ihren Tagen, sonst die reguläre — null, wenn es keinen Wechsel gibt. */
  variantForDate(date) {
    if (!this.dayVariant) return null;
    return this.onlyDates.includes(date) ? this.dayVariant : this.regularVariant;
  }

  applyVariantForDate(date) {
    const v = this.variantForDate(date);
    if (!v || v.id === String(this.variantId)) return;
    this.variantId = v.id;
    this.dataset.variantId = v.id;
    this.priceCents = v.priceCents;
    this.dataset.duration = v.duration;
    if (this.refs.durationLabel) this.refs.durationLabel.textContent = v.duration;
  }

  /** "01.10." aus "2026-10-01". */
  shortDate(iso) {
    const [, m, d] = String(iso).split('-');
    return `${d}.${m}.`;
  }

  /** Zeitraum eines Aktions-Eintrags in Worten, aus Sicht von heute. */
  promoRangeText(entry) {
    if (entry.von === entry.bis) return `am ${this.shortDate(entry.von)}`;
    if (entry.von <= this.todayStr()) return `bis ${this.shortDate(entry.bis)}`;
    return `vom ${this.shortDate(entry.von)} bis ${this.shortDate(entry.bis)}`;
  }

  /**
   * Nächster Aktionstag, der gezielt für diese Anwendung gilt (Eintrag mit
   * "produkte"/"varianten"), damit der Hinweis auch an Tagen ohne Aktion
   * erscheint. Allgemeine Aktionen bleiben außen vor — die sieht man beim Blättern.
   */
  nextTargetedPromo() {
    const today = this.todayStr();
    const pid = String(this.productId || '');
    const current = { id: String(this.variantId || ''), priceCents: this.priceCents, duration: this.dataset.duration || '' };
    const candidates = [current];
    if (this.dayVariant && this.dayVariant.id !== current.id) candidates.push(this.dayVariant);
    let best = null;
    this.promos.forEach((p) => {
      if (!(p.prozent > 0) || p.bis < today || !this.promoLive(p)) return;
      let variant = null;
      if (p.varianten.length) {
        variant = candidates.find((c) => p.varianten.includes(c.id)) || null;
      } else if (p.produkte.length && !p.nurSession && p.produkte.includes(pid)) {
        variant = current;
      }
      if (!variant) return;
      const day = p.von > today ? p.von : today;
      if (!best || day < best.day) best = { entry: p, day, variant };
    });
    return best;
  }

  /**
   * Hinweis unter der Zusammenfassung: Aktionspreis gilt nur am Aktionstag,
   * an anderen Tagen der reguläre Preis. Das Datum bleibt frei wählbar.
   */
  renderPromoNote(percent) {
    const note = this.refs.promoNote;
    if (!note) return;
    note.textContent = '';
    note.hidden = true;
    if (this.instant || this.priceCents <= 0 || this.upsell.length) return;
    const regular = this.formatMoney(this.priceCents);
    const onDayVariant = this.dayVariant && String(this.variantId) === this.dayVariant.id;
    if (percent) {
      const entry = this.promoEntry();
      if (!entry) return;
      const otherDays = onDayVariant
        ? `an anderen Tagen buchst du ${this.regularVariant.duration || 'die reguläre Anwendung'} zum regulären Preis von ${this.formatMoney(
            this.regularVariant.priceCents
          )}.`
        : `an anderen Tagen gilt der reguläre Preis von ${regular}.`;
      note.textContent = `${entry.label ? `${entry.label}: ` : ''}Dieser Preis gilt nur für Termine ${this.promoRangeText(
        entry
      )} – ${otherDays}`;
      note.hidden = false;
      return;
    }
    const next = this.nextTargetedPromo();
    if (!next || next.day === this.selectedDate) return;
    const promoCents = this.discountedCents(next.variant.priceCents, next.entry.prozent);
    const promoPrice = promoCents > 0 ? `für ${this.formatMoney(promoCents)}` : 'kostenlos';
    const otherVariant = next.variant.id !== String(this.variantId);
    const text = document.createElement('span');
    text.textContent = otherVariant
      ? `${next.entry.label ? `${next.entry.label} ` : ''}${this.promoRangeText(next.entry)}: ${next.variant.duration} ${promoPrice} – an diesem Tag buchst du ${
          this.dataset.duration || 'die reguläre Anwendung'
        } zum regulären Preis von ${regular}.`
      : `${next.entry.label ? `${next.entry.label} ` : ''}${this.promoRangeText(
          next.entry
        )}: ${promoPrice} statt ${regular} – für diesen Tag gilt der reguläre Preis.`;
    const jump = document.createElement('button');
    jump.type = 'button';
    jump.className = 'lhc-bk-promo-note__jump';
    jump.textContent = `Zum ${this.shortDate(next.day)} wechseln`;
    jump.addEventListener('click', () => this.goToDate(next.day));
    note.append(text, jump);
    note.hidden = false;
  }

  /** Kaufmännisch gerundet, damit die Anzeige zum Warenkorb passt. */
  discountedCents(cents, percent = this.promoPercent()) {
    if (!percent) return cents;
    return Math.round((cents * (100 - percent)) / 100);
  }

  /**
   * Preiszeile einer Anwendung: ohne Aktion nur der Preis, mit Aktion der
   * durchgestrichene Originalpreis plus hervorgehobener Aktionspreis.
   */
  renderUpsellPrice(item) {
    if (!item.priceEl) return;
    const percent = this.promoPercent(this.selectedDate, item.productId, item.variantId);
    item.priceEl.textContent = '';
    if (!percent || item.priceCents <= 0) {
      item.priceEl.textContent = this.formatMoney(item.priceCents);
      item.priceEl.removeAttribute('data-promo');
      return;
    }
    const alt = document.createElement('s');
    alt.className = 'lhc-bk-price__was';
    alt.textContent = this.formatMoney(item.priceCents);
    const now = document.createElement('span');
    now.className = 'lhc-bk-price__now';
    now.textContent = this.formatMoney(this.discountedCents(item.priceCents, percent));
    // Screenreader hören sonst nur zwei Beträge ohne Zusammenhang.
    const sr = document.createElement('span');
    sr.className = 'visually-hidden';
    sr.textContent = `, reduziert um ${percent} Prozent auf`;
    item.priceEl.append(alt, sr, now);
    item.priceEl.setAttribute('data-promo', String(percent));
  }

  renderPromoPrices() {
    this.upsell.forEach((item) => this.renderUpsellPrice(item));
    const hint = this.refs.promoHint;
    if (hint) {
      const percent = this.promoPercent();
      const label = this.promos.find((p) => this.selectedDate >= p.von && this.selectedDate <= p.bis)?.label;
      hint.hidden = !percent || this.upsell.length === 0;
      hint.textContent = percent
        ? `${label ? `${label}: ` : ''}${percent} % Rabatt auf alle Anwendungen an diesem Termin. Der Abzug erscheint im Warenkorb.`
        : '';
    }
    this.updatePriceDisplay();
  }

  /* --- Zeitrechnung im Session-Fenster --- */

  toMinutes(hhmm) {
    const [h, m] = String(hhmm || '').split(':').map(Number);
    return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : NaN;
  }

  toHhmm(minutes) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  /** Fenster der gewählten Session: [Start, Ende) in Minuten. */
  sessionWindow() {
    if (!this.selectedTime) return null;
    const start = this.toMinutes(this.selectedTime);
    const slot = (this.days.get(this.selectedDate) || []).find(
      (s) => (s.startTime || '').split(' ')[1] === this.selectedTime
    );
    const dur = Number(slot?.duration) || 0;
    if (!Number.isFinite(start) || !dur) return null;
    return { start, end: start + dur };
  }

  /** Behandlungs-Slots, die vollständig ins Session-Fenster passen. */
  eligibleUpsellSlots(item) {
    const win = this.sessionWindow();
    if (!win) return [];
    return (item.days.get(this.selectedDate) || [])
      .filter((s) => s.status === 'available' && s.remainingSlots !== 0)
      .map((s) => ({
        time: (s.startTime || '').split(' ')[1],
        remaining: typeof s.remainingSlots === 'number' ? s.remainingSlots : 1,
        duration: Number(s.duration) || 0,
      }))
      .filter((s) => {
        const start = this.toMinutes(s.time);
        return Number.isFinite(start) && start >= win.start && start + s.duration <= win.end;
      })
      .sort((a, b) => this.toMinutes(a.time) - this.toMinutes(b.time));
  }

  /**
   * Alle bereits gewählten Behandlungszeiten als Intervalle — jeder Eintrag
   * bindet genau eine Person. (exceptItem, exceptUnit) bleibt außen vor,
   * damit eine Zeile ihre eigene Wahl nicht selbst blockiert.
   */
  occupiedIntervals(exceptItem = null, exceptUnit = -1) {
    const out = [];
    this.upsell.forEach((i) => {
      if (i.qty <= 0) return;
      i.times.forEach((t, unit) => {
        if (!t || (i === exceptItem && unit === exceptUnit)) return;
        const start = this.toMinutes(t.time);
        const end = start + (t.duration || 0);
        if (Number.isFinite(start) && end > start) out.push({ start, end, title: i.title, item: i, time: t.time });
      });
    });
    return out;
  }

  /**
   * Höchste Zahl gleichzeitig laufender Anwendungen, wenn [start, end) zu den
   * Intervallen dazukäme. Mehr als "Anzahl Personen" gleichzeitig geht nicht —
   * und welche Person welche Anwendung nimmt, muss dafür niemand festlegen:
   * ein Plan ist genau dann auf P Personen aufteilbar, wenn nie mehr als P
   * Anwendungen parallel laufen (Intervalle brauchen nur so viele "Farben"
   * wie ihre dichteste Stelle).
   */
  maxConcurrentWith(intervals, start, end) {
    const points = [start, ...intervals.map((r) => r.start).filter((p) => p > start && p < end)];
    return Math.max(...points.map((p) => 1 + intervals.filter((r) => r.start <= p && p < r.end).length));
  }

  /** Statuszeile einer Anwendung: gewählte Zeiten, sonst freie Termine. */
  renderUpsellAvail(item) {
    if (!item.availEl || !this.selectedTime) return;
    const slots = this.eligibleUpsellSlots(item);
    item.availEl.classList.toggle('is-out', slots.length === 0);
    const picked = item.times.slice(0, item.qty).filter(Boolean);
    item.availEl.textContent = slots.length
      ? picked.length
        ? `Gewählt: ${picked.map((t) => `${t.time} Uhr`).join(', ')}`
        : `${slots.length === 1 ? '1 Termin' : `${slots.length} Termine`} im Zeitfenster frei`
      : 'In diesem Zeitfenster nicht verfügbar';
  }

  changeUpsellQty(item, delta) {
    // Nicht mehr Einheiten als Personen: jede laufende Anwendung bindet eine Person.
    const next = Math.min(Math.max(0, item.qty + delta), Math.min(item.max, this.quantity));
    if (next === item.qty) return;
    item.qty = next;
    // Zeitliste auf die Menge bringen. Beim Schrumpfen bleiben gewählte Zeiten
    // bevorzugt erhalten (Verdichten) — Einheiten sind untereinander austauschbar.
    let times = item.times.slice(0, next);
    if (item.times.length > next) {
      times = item.times.filter(Boolean).slice(0, next);
    }
    while (times.length < next) times.push(null);
    item.times = times;
    this.renderUpsellItem(item);
    // Die Sperren hängen voneinander ab → alle Raster neu zeichnen.
    this.upsell.forEach((i) => this.renderUpsellTimes(i));
    this.renderUpsellAvail(item);
    this.updatePriceDisplay();
    this.renderCta();
  }

  chooseUpsellTime(item, unit, time, slot) {
    item.times[unit] = { time, duration: slot.duration };
    item.duration = slot.duration;
    this.renderUpsellItem(item);
    this.upsell.forEach((i) => this.renderUpsellTimes(i));
    this.renderUpsellAvail(item);
    this.updatePriceDisplay();
    this.renderCta();
    this.clearMessage();
  }

  /**
   * Zeitliste einer Anwendung gegen die aktuell wählbaren Slots abgleichen:
   * Weggefallenes fliegt raus, capLeft zählt je Uhrzeit die Restplätze herunter
   * (überzählige Einheiten auf derselben Uhrzeit verlieren ihre Wahl statt still
   * zu überbuchen), und die Länge folgt der Menge.
   */
  syncUpsellTimes(item, slots = this.eligibleUpsellSlots(item)) {
    const capLeft = new Map(slots.map((s) => [s.time, s.remaining]));
    let times = item.times.map((t) => {
      if (!t) return null;
      const left = capLeft.get(t.time) ?? 0;
      if (left <= 0) return null;
      capLeft.set(t.time, left - 1);
      return t;
    });
    if (times.length > item.qty) {
      // Beim Kappen Überlebende nach vorn — eine noch gültige Wahl darf nicht
      // zugunsten einer Lücke verfallen.
      times = times.filter(Boolean);
    }
    times = times.slice(0, item.qty);
    while (times.length < item.qty) times.push(null);
    item.times = times;
  }

  /** Zeit-Raster einer Anwendung zeichnen — eine Auswahlzeile je Einheit (= Person). */
  renderUpsellTimes(item) {
    const { timesEl, timeGridEl } = item;
    if (!timesEl || !timeGridEl) return;
    if (item.qty <= 0) {
      // Menge 0 heißt: nichts gewählt. Ohne diese Zeile überlebt eine alte
      // Uhrzeit die Klemmung (z. B. Session-Slot-Wechsel ohne passenden Termin)
      // und würde vom Plus-Button ungeprüft wiederbelebt.
      item.times = [];
      timesEl.hidden = true;
      timeGridEl.textContent = '';
      return;
    }
    timesEl.hidden = false;
    timeGridEl.textContent = '';
    if (item.timesLabelEl) {
      item.timesLabelEl.textContent =
        item.qty === 1 ? `Wähle die Uhrzeit für ${item.title}` : `Wähle ${item.qty} Uhrzeiten für ${item.title}`;
    }

    const slots = this.eligibleUpsellSlots(item);
    if (!slots.length) {
      item.times = new Array(item.qty).fill(null); // nichts im Fenster → nichts gewählt
      const note = document.createElement('p');
      note.className = 'lhc-bk-upsell__times-note';
      note.textContent = 'In diesem Zeitfenster ist kein Termin frei.';
      timeGridEl.appendChild(note);
      return;
    }

    this.syncUpsellTimes(item, slots);

    for (let unit = 0; unit < item.qty; unit += 1) {
      const group = document.createElement('div');
      group.className = 'lhc-bk-upsell__unit';
      group.setAttribute('role', 'radiogroup');
      group.setAttribute(
        'aria-label',
        item.qty === 1 ? `Uhrzeit für ${item.title}` : `Uhrzeit für ${item.title}, Termin ${unit + 1} von ${item.qty}`
      );
      if (item.qty > 1) {
        const label = document.createElement('p');
        label.className = 'lhc-bk-upsell__unit-label';
        label.textContent = `Termin ${unit + 1}`;
        group.appendChild(label);
      }
      const row = document.createElement('div');
      row.className = 'lhc-bk-upsell__slots';

      const others = this.occupiedIntervals(item, unit);
      slots.forEach((slot) => {
        const start = this.toMinutes(slot.time);
        const end = start + slot.duration;
        const chosenHere = item.times[unit]?.time === slot.time;
        // Kapazität: Einheiten derselben Anwendung auf derselben Uhrzeit
        const stacked = others.filter((r) => r.item === item && r.time === slot.time).length;
        const full = !chosenHere && stacked >= slot.remaining;
        // Personen: nie mehr parallele Anwendungen als gewählte Personen
        const overlapping = others.filter((r) => start < r.end && end > r.start);
        const crowd = !chosenHere && !full && this.maxConcurrentWith(overlapping, start, end) > this.quantity;
        const reason = full
          ? ' — keine freien Plätze mehr zu dieser Zeit'
          : crowd
            ? this.quantity === 1
              ? ` — belegt durch ${overlapping[0].title}`
              : ' — alle Personen sind zu dieser Zeit in Anwendungen'
            : '';
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'lhc-bk-upsell__time';
        chip.textContent = slot.time;
        chip.setAttribute('role', 'radio');
        chip.setAttribute('aria-checked', String(chosenHere));
        chip.setAttribute('aria-disabled', String(full || crowd));
        chip.setAttribute('aria-label', `${slot.time} bis ${this.toHhmm(end)}${reason}`);
        if (!full && !crowd) chip.addEventListener('click', () => this.chooseUpsellTime(item, unit, slot.time, slot));
        row.appendChild(chip);
      });
      group.appendChild(row);
      timeGridEl.appendChild(group);
    }
  }

  renderUpsellItem(item) {
    if (item.countEl) item.countEl.textContent = String(item.qty);
    // Vor dem Deaktivieren merken — danach wäre activeElement bereits <body>.
    const active = document.activeElement;
    if (item.plusEl) item.plusEl.disabled = !this.selectedTime || item.qty >= Math.min(item.max, this.quantity);
    if (item.minusEl) item.minusEl.disabled = item.qty <= 0;
    // Der geklickte Stepper-Button kann sich selbst deaktiviert haben — dann fiele
    // der Fokus auf <body> (gleiches Muster wie bei den Kalender-Monatspfeilen).
    if (active === item.plusEl && item.plusEl.disabled && item.minusEl && !item.minusEl.disabled) {
      item.minusEl.focus();
    } else if (active === item.minusEl && item.minusEl.disabled && item.plusEl && !item.plusEl.disabled) {
      item.plusEl.focus();
    }
    item.el.classList.toggle('is-selected', item.qty > 0);
  }

  /**
   * Jede Anwendung hat ihren eigenen Sesami-Standort — nicht den der Session
   * mitschicken, sonst antwortet Sesami mit 404 "Location not found".
   */
  ensureUpsellConfig(item) {
    if (!item.configPromise) {
      item.configPromise = this.withAuthRetry(() =>
        this.getJson(`${SESAMI_STOREFRONT}/api/v1/config/${item.productId}?&token=${encodeURIComponent(this.token)}`)
      ).then(
        (config) => {
          item.location = config?.locations?.[0] || null;
        },
        (error) => {
          item.configPromise = null; // beim nächsten Versuch erneut laden
          throw error;
        }
      );
    }
    return item.configPromise;
  }

  async fetchUpsellDay(item, date) {
    let pending = item.inflight.get(date);
    if (!pending) {
      pending = (async () => {
        await this.ensureUpsellConfig(item);
        await this.withAuthRetry(async () => {
          const params = new URLSearchParams({
            length: '1',
            start: date,
            accesstoken: this.token,
            shopId: this.shopId,
            productId: item.productId,
            variantId: item.variantId,
            quantity: '1',
            timezone: this.timezone,
          });
          if (item.location?.id) params.set('location', item.location.id);
          const data = await this.getJson(`${SESAMI_STOREFRONT}/availabilities?${params}`);
          if (!Array.isArray(data)) throw new Error('Sesami Availabilities: unerwartetes Antwortformat');
          item.days.set(date, data.find((d) => d?.date === date)?.slots || []);
        });
      })().finally(() => item.inflight.delete(date));
      item.inflight.set(date, pending);
    }
    return pending;
  }

  /** Restkontingent je Anwendung für den gewählten Slot laden und Stepper begrenzen. */
  refreshUpsell() {
    if (!this.upsell.length) return;
    const date = this.selectedDate;
    const time = this.selectedTime;
    const status = this.refs.upsellStatus;
    if (status && !time) status.textContent = '';
    // Phase 1: Die Jobs gleichen NUR den Zustand ab (Daten laden, max/qty/times
    // klemmen). Gezeichnet wird erst, wenn alle durch sind — sonst rechnet ein
    // früh gezeichnetes Raster seine Sperren gegen Zeiten eines späteren Items,
    // die gleich darauf verworfen werden.
    const fehler = new Set();
    const jobs = this.upsell.map(async (item) => {
      if (!time) {
        item.max = 0;
        item.times = [];
        // Auch die Menge folgt sofort der Personenzahl — sonst zeigen Zähler
        // und Gesamtsumme bis zur nächsten Slot-Wahl zu viele Einheiten.
        if (item.qty > this.quantity) item.qty = this.quantity;
        item.availEl?.classList.remove('is-out');
        if (item.availEl) item.availEl.textContent = 'Bitte wähle zuerst eine Uhrzeit.';
        fehler.add(item); // Text nicht vom Sammel-Zeichnen überschreiben lassen
        return;
      }
      if (item.availEl) item.availEl.textContent = 'Prüfe Verfügbarkeit …';
      try {
        if (!item.days.has(date)) await this.fetchUpsellDay(item, date);
      } catch (error) {
        console.error('[lhc-booking]', error);
        if (this.selectedDate === date && this.selectedTime === time) {
          item.max = 0;
          item.qty = 0;
          item.times = [];
          if (item.availEl) item.availEl.textContent = 'Verfügbarkeit konnte nicht geladen werden.';
          fehler.add(item);
        }
        return;
      }
      // Antwort verwerfen, wenn der Nutzer inzwischen umgewählt hat.
      if (this.selectedDate !== date || this.selectedTime !== time) return;
      // Die Anwendung läuft innerhalb des Session-Fensters zu einer eigenen
      // Uhrzeit — nicht mehr zwingend zum Session-Start.
      const slots = this.eligibleUpsellSlots(item);
      item.duration = slots[0]?.duration || item.duration;
      // Kapazitätsgrenze: Summe der Restplätze im Fenster — mehr Einheiten sind
      // nicht unterzubringen. Die Personenzahl deckelt zusätzlich (Stepper und
      // changeUpsellQty), steht aber bewusst nicht in item.max: sie ist keine
      // Eigenschaft der Verfügbarkeit.
      item.max = slots.reduce((sum, s) => sum + s.remaining, 0);
      const cap = Math.min(item.max, this.quantity);
      if (item.qty > cap) item.qty = cap;
      this.syncUpsellTimes(item, slots);
    });
    // Phase 2: einmal über alle zeichnen — gegen den fertig abgeglichenen Zustand.
    Promise.allSettled(jobs).then(() => {
      if (this.selectedDate !== date || this.selectedTime !== time) return;
      this.upsell.forEach((item) => {
        this.renderUpsellItem(item);
        this.renderUpsellTimes(item);
        if (!fehler.has(item)) this.renderUpsellAvail(item);
      });
      this.updatePriceDisplay();
      this.renderCta();
      // Eine gesammelte Ansage statt einer pro Anwendung.
      if (status && time) {
        status.textContent = `Verfügbarkeit der Anwendungen für ${time} Uhr aktualisiert.`;
      }
    });
  }

  /* ---------- Wizard ---------- */

  setStep(step, push = true) {
    this.step = step;
    this.dataset.step = step;
    Object.entries(this.panels).forEach(([name, el]) => {
      el.hidden = name !== step;
    });
    if (this.refs.summary) this.refs.summary.hidden = step === 'bestaetigen' || step === 'fertig';
    if (this.refs.cta) this.refs.cta.hidden = step === 'fertig';
    if (this.refs.intro) this.refs.intro.hidden = step === 'fertig';
    if (this.refs.steps) this.refs.steps.hidden = step === 'fertig';

    const copy = this.copy?.[step];
    if (copy) {
      if (this.refs.introTitle) this.refs.introTitle.textContent = copy.title;
      if (this.refs.introLead) this.refs.introLead.textContent = copy.lead;
    }

    const stepNum = { termin: 2, daten: 3, bestaetigen: 4, fertig: 5 }[step] || 2;
    this.updateSteps(stepNum);
    this.renderCta();
    this.clearMessage();

    if (push) {
      const url = new URL(window.location.href);
      if (step === 'termin') url.searchParams.delete('step');
      else url.searchParams.set('step', step);
      // Trägt die URL eine Link-Vorauswahl, diese auf die aktuelle Wahl nachziehen —
      // sonst springt ein Reload mit der alten Link-Uhrzeit direkt nach „daten".
      const linked = ['datum', 'date', 'uhrzeit', 'time'].some((k) => url.searchParams.has(k));
      if (step !== 'termin' && linked && this.selectedDate && this.selectedTime) {
        ['date', 'time', 'quantity'].forEach((k) => url.searchParams.delete(k));
        url.searchParams.set('datum', this.selectedDate);
        url.searchParams.set('uhrzeit', this.selectedTime);
        if (this.quantity > 1) url.searchParams.set('personen', String(this.quantity));
        else url.searchParams.delete('personen');
      }
      history.pushState({ lhcStep: step }, '', url);
    }
    if (step !== 'termin') window.scrollTo({ top: this.offsetTop - 80, behavior: 'smooth' });
  }

  updateSteps(activeNum) {
    this.refs.steps?.querySelectorAll('[data-step]').forEach((item) => {
      const num = Number(item.dataset.step);
      const numEl = item.querySelector('.lhc-bk-steps__num');
      item.classList.toggle('is-done', num < activeNum);
      item.classList.toggle('is-active', num === activeNum);
      if (num === activeNum) item.setAttribute('aria-current', 'step');
      else item.removeAttribute('aria-current');
      if (numEl) numEl.textContent = num < activeNum ? '✓' : String(num);
    });
  }

  renderCta() {
    const { submit, submitLabel, back } = this.refs;
    if (submitLabel) {
      submitLabel.textContent = this.booking
        ? 'Wird gebucht …'
        : this.step === 'bestaetigen'
          ? 'Verbindlich buchen'
          : this.step === 'termin' && this.upsellCount() > 0
            ? 'Zur Kasse'
            : 'Weiter';
    }
    // Ohne buchbare Termine gibt es nichts weiterzugehen — sonst würde die
    // „Bitte wähle eine Uhrzeit"-Meldung die eigentliche Erklärung überschreiben.
    // Ebenso gesperrt, solange eine gewählte Anwendung noch keine Uhrzeit hat.
    if (submit) {
      submit.disabled =
        this.noAvailability === true ||
        this.sessionOnlyDay === true ||
        (this.step === 'termin' && this.upsell.some((item) => item.qty > 0 && item.times.filter(Boolean).length < item.qty));
    }
    if (back) {
      const label = back.querySelector('span');
      if (label) label.textContent = 'Zurück';
      back.setAttribute('href', this.step === 'termin' ? this.backUrl : '#');
    }
  }

  onBack(event) {
    if (this.step === 'termin') return; // normaler Link zur Übersicht
    event.preventDefault();
    this.setStep(this.step === 'bestaetigen' ? 'daten' : 'termin');
  }

  onSubmit() {
    if (this.booking) return;
    if (this.step === 'termin') {
      if (!this.ready || !this.selectedTime) {
        this.showMessage('Bitte wähle zuerst eine Uhrzeit.', true);
        return;
      }
      const ohneZeit = this.upsell.find((item) => item.qty > 0 && item.times.filter(Boolean).length < item.qty);
      if (ohneZeit) {
        this.showMessage(
          ohneZeit.qty === 1
            ? `Bitte wähle eine Uhrzeit für ${ohneZeit.title}.`
            : `Bitte wähle alle ${ohneZeit.qty} Uhrzeiten für ${ohneZeit.title}.`,
          true
        );
        return;
      }
      if (this.upsellCount() > 0) {
        // Session + zugebuchte Anwendungen müssen bezahlt werden → Checkout.
        this.bookViaCart();
      } else if (this.instant) {
        this.fillSummaryForData();
        this.setStep('daten');
      } else {
        this.bookViaCart();
      }
      return;
    }
    if (this.step === 'daten') {
      if (!this.validateForm()) return;
      this.fillConfirm();
      this.setStep('bestaetigen');
      return;
    }
    if (this.step === 'bestaetigen') {
      if (this.refs.agb && !this.refs.agb.checked) {
        this.setFieldError('agb', 'Bitte akzeptiere die AGB und Stornobedingungen.');
        return;
      }
      this.bookInstant();
    }
  }

  fillSummaryForData() {
    const { summaryMeta } = this.refs;
    if (!summaryMeta) return;
    const parts = [`${this.formatDate(this.selectedDate)} · ${this.selectedTime} Uhr`];
    if (this.dataset.duration) parts.push(this.dataset.duration);
    if (this.location?.name) parts.push(this.location.name);
    // Gruppentermine: die Personenauswahl liegt nur auf der Terminwahl — hier zeigen,
    // für wie viele gebucht wird (auch nach dem Direkteinstieg per step=daten).
    if (this.groupMax > 1) parts.push(this.quantity === 1 ? '1 Person' : `${this.quantity} Personen`);
    summaryMeta.textContent = parts.join(' · ');
  }

  /* ---------- Formular ---------- */

  readForm() {
    const r = this.refs;
    const digits = (r.phoneNumber?.value || '').replace(/\D/g, '').replace(/^0+/, '');
    return {
      firstName: (r.firstName?.value || '').trim(),
      lastName: (r.lastName?.value || '').trim(),
      email: (r.email?.value || '').trim(),
      phoneCode: r.phoneCode?.value || '+49',
      phoneDigits: digits,
    };
  }

  validateForm() {
    const data = this.readForm();
    const required = this.requiredFields || [];
    let firstBad = null;
    const fail = (field, message, ref) => {
      this.setFieldError(field, message);
      if (!firstBad) firstBad = ref;
    };

    this.clearFieldError('firstName');
    this.clearFieldError('lastName');
    this.clearFieldError('email');
    this.clearFieldError('phone');

    if (required.includes('firstName') && !data.firstName) {
      fail('firstName', 'Vorname ist erforderlich.', this.refs.firstName);
    }
    if (required.includes('lastName') && !data.lastName) {
      fail('lastName', 'Nachname ist erforderlich.', this.refs.lastName);
    }
    if (required.includes('email')) {
      if (!data.email) fail('email', 'E-Mail-Adresse ist erforderlich.', this.refs.email);
      else if (!EMAIL_RE.test(data.email)) fail('email', 'Bitte gib eine gültige E-Mail-Adresse ein.', this.refs.email);
    }
    if (required.includes('phone') && data.phoneDigits.length < 5) {
      fail('phone', 'Bitte gib eine gültige Mobilnummer ein.', this.refs.phoneNumber);
    }

    if (firstBad) {
      firstBad.focus();
      return false;
    }
    this.customer = data;
    return true;
  }

  setFieldError(field, message) {
    const el = this.querySelector(`[data-error="${field}"]`);
    if (el) {
      el.textContent = message;
      el.hidden = false;
    }
    const input = this.querySelector(`[data-field="${field}"]`);
    if (input) input.setAttribute('aria-invalid', 'true');
  }

  clearFieldError(field) {
    const el = this.querySelector(`[data-error="${field}"]`);
    if (el) {
      el.textContent = '';
      el.hidden = true;
    }
    const input = this.querySelector(`[data-field="${field}"]`);
    if (input) input.removeAttribute('aria-invalid');
  }

  fillConfirm() {
    const r = this.refs;
    const c = this.customer;
    if (r.confirmDate) r.confirmDate.textContent = this.formatDate(this.selectedDate);
    if (r.confirmTime) r.confirmTime.textContent = `${this.selectedTime} Uhr (${this.timezone})`;
    if (r.confirmFirst) r.confirmFirst.textContent = c.firstName;
    if (r.confirmLast) r.confirmLast.textContent = c.lastName;
    if (r.confirmEmail) r.confirmEmail.textContent = c.email;
    if (r.confirmPhone) r.confirmPhone.textContent = `${c.phoneCode} ${c.phoneDigits}`;
  }

  /* ---------- Weg B: Sofortbuchung ---------- */

  async bookInstant() {
    this.booking = true;
    this.refs.submit?.setAttribute('aria-busy', 'true');
    this.renderCta();
    this.clearMessage();

    try {
      const appointment = await this.withAuthRetry(() => this.postBook());
      try {
        sessionStorage.setItem(`lhc:booked:${this.productId}`, `${this.selectedDate}|${this.selectedTime}`);
      } catch (error) {
        /* ohne Storage entfällt nur der Reload-Schutz in applyUrlWish */
      }
      this.renderSuccess(appointment);
      this.setStep('fertig');
      // Kein erneutes Absenden nach Reload/Zurück
      history.replaceState({ lhcStep: 'fertig' }, '', window.location.pathname + '?view=buchung');
    } catch (error) {
      console.error('[lhc-booking]', error);
      this.booking = false;
      this.refs.submit?.removeAttribute('aria-busy');
      this.renderCta();
      if (error && error.field === 'phone') {
        this.setStep('daten');
        this.setFieldError('phone', 'Diese Mobilnummer wurde nicht akzeptiert. Bitte prüfe sie.');
        this.refs.phoneNumber?.focus();
      } else if (error && error.slotUnavailable) {
        this.days.delete(this.selectedDate);
        this.setStep('termin');
        await this.goToDate(this.selectedDate);
        this.showMessage('Dieses Zeitfenster wurde gerade vergeben. Bitte wähle eine andere Uhrzeit.', true);
      } else {
        this.showMessage('Die Buchung konnte nicht abgeschlossen werden. Bitte versuche es erneut.', true);
      }
    } finally {
      this.booking = false;
    }
  }

  async postBook() {
    const c = this.customer;
    const body = {
      sessionId: this.sessionId,
      productId: this.productId,
      variantId: this.variantId,
      quantity: String(this.quantity),
      locale: this.locale === 'de' ? 'de-DE' : this.locale,
      timezone: this.timezone,
      date: this.selectedDate,
      time: this.selectedTime,
      customer: {
        firstName: c.firstName || undefined,
        lastName: c.lastName || undefined,
        email: c.email || undefined,
        phone: c.phoneDigits ? `${c.phoneCode}${c.phoneDigits}` : undefined,
      },
    };
    if (this.location?.id) body.location = this.location.id;

    const response = await fetch(`${SESAMI_APP}/api/v1/book`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: '*/*',
        Authorization: `Bearer ${this.token}`,
      },
      body: JSON.stringify(body),
    });
    if (response.status !== 200) {
      let payload = null;
      try {
        payload = await response.json();
      } catch (e) {
        /* Antwort ohne JSON */
      }
      const error = new Error(`Sesami Book ${response.status}`);
      error.status = response.status;
      error.field = payload?.field;
      error.slotUnavailable = response.status === 410 || payload?.error === 'unavailable_slot';
      throw error;
    }
    return response.json();
  }

  renderSuccess(appointment) {
    const r = this.refs;
    const when = `${this.formatDate(this.selectedDate)} · ${this.selectedTime} Uhr`;
    if (r.successWhen) r.successWhen.textContent = when;
    if (r.manageLink) {
      if (appointment?.customerManagementLink) {
        r.manageLink.href = appointment.customerManagementLink;
        r.manageLink.closest('.lhc-bk-success__manage')?.removeAttribute('hidden');
      } else {
        r.manageLink.closest('.lhc-bk-success__manage')?.setAttribute('hidden', '');
      }
    }
    if (r.icsLink) {
      const ics = this.buildIcs(appointment);
      if (ics) r.icsLink.href = ics;
      else r.icsLink.hidden = true;
    }
  }

  buildIcs(appointment) {
    const startIso = appointment?.datetime;
    if (!startIso) return null;
    const start = new Date(startIso);
    const minutes = Number(appointment.duration) || 30;
    const end = new Date(start.getTime() + minutes * 60000);
    const stamp = (d) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const escape = (s) => String(s || '').replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n');
    const lines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Longevity Health Club//Booking//DE',
      'BEGIN:VEVENT',
      `UID:${appointment._id || Date.now()}@longevity-health-shop.de`,
      `DTSTAMP:${stamp(new Date(appointment.createdAt || Date.now()))}`,
      `DTSTART:${stamp(start)}`,
      `DTEND:${stamp(end)}`,
      `SUMMARY:${escape(this.productTitle)}`,
      `LOCATION:${escape(this.location?.name || '')}`,
      'END:VEVENT',
      'END:VCALENDAR',
    ];
    return 'data:text/calendar;charset=utf-8,' + encodeURIComponent(lines.join('\r\n'));
  }

  /* ---------- Weg A: Reservierung(en) + Warenkorb ---------- */

  async bookViaCart() {
    this.booking = true;
    this.refs.submit?.setAttribute('aria-busy', 'true');
    this.renderCta();
    this.clearMessage();

    // Auswahl zu Beginn einfrieren — die UI bleibt während der Requests bedienbar,
    // Reservierung und Cart-Position dürfen aber nicht auseinanderlaufen.
    const date = this.selectedDate;
    const time = this.selectedTime;
    const persons = this.quantity;
    // Jede Anwendung hat ihre eigene Uhrzeit im Session-Fenster.
    // Je Anwendung die Einheiten nach Uhrzeit bündeln: gleiche Uhrzeit = eine
    // Reservierung und eine Warenkorb-Zeile mit entsprechender Menge; verschiedene
    // Uhrzeiten derselben Anwendung werden eigene Zeilen (Properties je Zeile).
    const chosen = this.upsell
      .filter((item) => item.qty > 0)
      .map((item) => {
        const perTime = new Map();
        item.times.filter(Boolean).forEach((t) => {
          perTime.set(t.time, (perTime.get(t.time) || 0) + 1);
        });
        return { item, perTime };
      });

    try {
      // Session zuerst reservieren, danach die zugebuchten Anwendungen. Bereits
      // angelegte Reservierungen werden bei einem Retry wiederverwendet (reserveOnce) —
      // sonst belegt jeder Fehlversuch das Kontingent erneut und sperrt den Nutzer aus.
      const sessionRes = await this.reserveOnce(this.productId, this.variantId, persons, date, time, this.location);
      const lines = [
        { id: Number(this.variantId), quantity: persons, properties: this.lineProperties(sessionRes.token, date, time, this.location) },
      ];
      for (const { item, perTime } of chosen) {
        await this.ensureUpsellConfig(item);
        for (const [itemTime, count] of perTime) {
          const res = await this.reserveOnce(item.productId, item.variantId, count, date, itemTime, item.location);
          lines.push({
            id: Number(item.variantId),
            quantity: count,
            properties: this.lineProperties(res.token, date, itemTime, item.location),
          });
        }
      }
      await this.removeStaleBookingLines(this.cartOwnedProductIds(chosen.map(({ item }) => item)));
      await this.cartAddLines(lines);
      window.location.assign(this.checkoutUrl);
    } catch (error) {
      console.error('[lhc-booking]', error);
      this.booking = false;
      this.refs.submit?.removeAttribute('aria-busy');
      this.renderCta();
      if (error && error.slotUnavailable) {
        this.days.delete(this.selectedDate);
        this.upsell.forEach((item) => item.days.delete(this.selectedDate));
        await this.goToDate(this.selectedDate);
        this.showMessage(
          this.upsellCount() > 0
            ? 'Eines der gewählten Zeitfenster ist nicht mehr verfügbar. Bitte prüfe Uhrzeit und Anwendungen.'
            : 'Dieses Zeitfenster wurde gerade vergeben. Bitte wähle eine andere Uhrzeit.',
          true
        );
      } else {
        this.showMessage('Die Buchung konnte nicht abgeschlossen werden. Bitte versuche es erneut.', true);
      }
    }
  }

  async createReservation(productId, variantId, quantity, date = this.selectedDate, time = this.selectedTime, location = this.location) {
    const response = await fetch(`${SESAMI_APP}/api/v1/reservation?accesstoken=${encodeURIComponent(this.token)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        product: productId,
        variantId: variantId,
        quantity: String(quantity),
        location: location?.id || '',
        date,
        time,
        timezone: this.timezone,
      }),
    });
    if (!response.ok) {
      const error = new Error(`Sesami Reservation ${response.status}`);
      error.status = response.status;
      error.slotUnavailable = response.status === 400 || response.status === 409 || response.status === 410;
      throw error;
    }
    const data = await response.json();
    if (!data?.token) throw new Error('Sesami Reservation: kein Token erhalten');
    return data;
  }

  /**
   * Reservierung anlegen — oder die aus einem vorigen, gescheiterten Versuch
   * wiederverwenden. Sie hält den Slot ohnehin ~10 Minuten; ein zweiter Aufruf
   * würde dasselbe Kontingent erneut belegen und den Nutzer selbst aussperren.
   */
  async reserveOnce(productId, variantId, quantity, date, time, location = this.location) {
    // Menge bewusst NICHT im Key: Ändert der Nutzer nach einem Fehlversuch die
    // Aufteilung (z. B. 2× 10:00 → je 1× 10:00 und 10:30), hielte die alte
    // 2er-Reservierung ihre Plätze weiter fest — eine neue Anfrage über dieselbe
    // Uhrzeit fände dann keine freien Plätze mehr und sperrte den Nutzer aus.
    // Eine bestehende Reservierung mit genügend Plätzen wird darum weiterverwendet.
    const key = [productId, variantId, date, time].join('|');
    const cached = this.reservations.get(key);
    if (cached && cached.quantity >= quantity && cached.expiresAt - Date.now() > 60000) return cached;
    const res = await this.withAuthRetry(() => this.createReservation(productId, variantId, quantity, date, time, location));
    const entry = { token: res.token, quantity, expiresAt: Date.now() + (Number(res.expiresIn) || 540) * 1000 };
    this.reservations.set(key, entry);
    return entry;
  }

  /**
   * Produkt-IDs, deren alte Cart-Positionen DIESE Buchungsseite aufräumen darf:
   * die Session selbst, die jetzt gewählten Anwendungen und alles, was diese
   * Seite früher schon einmal in den Warenkorb gelegt hat (sessionStorage) —
   * aber nie fremde Buchungen anderer Buchungsstrecken.
   */
  cartOwnedProductIds(chosenItems) {
    const key = `lhc:booking-added:${this.productId}`;
    let prev = [];
    try {
      prev = JSON.parse(sessionStorage.getItem(key) || '[]');
    } catch (error) {
      /* Storage blockiert → ohne Gedächtnis weiter */
    }
    const ids = new Set([String(this.productId), ...prev.map(String), ...chosenItems.map((i) => String(i.productId))]);
    try {
      sessionStorage.setItem(key, JSON.stringify([...ids]));
    } catch (error) {
      /* Storage blockiert */
    }
    return ids;
  }

  // Alte Buchungs-Positionen derselben Strecke entfernen (Browser-Zurück aus dem
  // Checkout + erneute Buchung würde sonst doppelte Termine in den Warenkorb legen).
  async removeStaleBookingLines(own) {
    try {
      const cart = await this.getJson(this.cartUrl);
      const stale = (cart.items || []).filter(
        (item) => own.has(String(item.product_id)) && item.properties && item.properties['_reservation_token']
      );
      for (const item of stale) {
        await fetch(this.cartChangeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        });
      }
    } catch (error) {
      // Cart-Bereinigung darf die Buchung nicht blockieren
      console.warn('[lhc-booking] Cart-Bereinigung fehlgeschlagen', error);
    }
  }

  /**
   * Line-Item-Properties wie beim Original-Widget. Namen sind lokalisiert und
   * kommen aus den Sesami-Übersetzungen (de: Datum / Uhrzeit / Zeitzone / Standort).
   * Übersetzungen sind shop-weit — sie gelten für Session und Anwendungen gleich.
   */
  lineProperties(reservationToken, date = this.selectedDate, time = this.selectedTime, location = this.location) {
    const t = this.config?.translations || {};
    const props = {};
    props[t.date || 'Datum'] = date;
    props[t.time || 'Uhrzeit'] = time;
    props[t.timezone || 'Zeitzone'] = this.timezone;
    if (location?.name) props[`_${t.location || 'Standort'}`] = location.name;
    props[t.resources || 'Ressourcen'] = '';
    props['_reservation_token'] = reservationToken;
    props['_is_migrated'] = 'true';
    props['_locale'] = this.locale;
    props['_session-id'] = this.sessionId;
    // Termindatum unter einem festen, sprachunabhängigen Key. Die Keys oben
    // stammen aus Sesamis Übersetzungen und heißen bei einem Sprachwechsel
    // plötzlich anders; eine Rabattregel würde dann stillschweigend ins Leere
    // greifen. Discount Functions lesen Keys exakt und können nicht suchen,
    // deshalb matcht die Aktionsregel auf diesen hier statt auf "Datum".
    props['_lhc_datum'] = date;
    return props;
  }

  async cartAddLines(items) {
    const response = await fetch(this.cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!response.ok) throw new Error(`Cart add ${response.status}`);
    return response.json();
  }

  /* ---------- UI-Helfer ---------- */

  showMessage(text, focus = false) {
    const { message } = this.refs;
    if (!message) return;
    message.textContent = '';
    message.hidden = false;
    // Die Live-Region muss im A11y-Tree sein, BEVOR sich der Text ändert —
    // sonst bleibt die Ansage bei NVDA/VoiceOver aus.
    requestAnimationFrame(() => {
      if (message.hidden) return;
      message.textContent = text;
      if (focus) message.focus();
    });
  }

  clearMessage() {
    const { message } = this.refs;
    if (!message) return;
    message.textContent = '';
    // Nicht ausblenden, solange der Fokus drinsteht — sonst geht er erneut verloren.
    if (document.activeElement !== message) message.hidden = true;
  }
}

if (!customElements.get('lhc-booking-termin')) {
  customElements.define('lhc-booking-termin', LhcBookingTermin);
}
